/**
 * Auth API client — login + refresh + dev-token.
 *
 * Lives next to the rest of `shared/api/*` for symmetry, but unlike the
 * domain endpoints it never relies on the request interceptor that
 * attaches a Bearer header — these endpoints are pre-auth (or peer-auth)
 * by definition.
 */

import { API_BASE_URL } from "./client";


export interface TokenPair {
  access_token:  string;
  refresh_token: string;
  token_type:    string;
  expires_in:    number;
  scopes:        string[];
  tenant_id:     string;
  user_id:       string;
}


export interface RefreshResponse {
  access_token:  string;
  refresh_token: string;
  token_type:    string;
  expires_in:    number;
}


export interface LoginRequest {
  email:        string;
  password:     string;
  tenant_slug?: string;
}


export interface OIDCConfigDto {
  enabled:        boolean;
  issuer:         string | null;
  authorize_url:  string | null;
  client_id:      string | null;
  audience:       string | null;
}


async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body:    JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    const msg = typeof detail === "object" && detail !== null && "detail" in detail
      ? String((detail as { detail: unknown }).detail)
      : `${response.status} ${response.statusText}`;
    const err = new Error(msg) as Error & { status: number };
    err.status = response.status;
    throw err;
  }
  return response.json() as Promise<T>;
}


export const authApi = {
  login(body: LoginRequest): Promise<TokenPair> {
    return postJson<TokenPair>("/auth/login", body);
  },

  refresh(refreshToken: string): Promise<RefreshResponse> {
    return postJson<RefreshResponse>("/auth/refresh", { refresh_token: refreshToken });
  },

  /**
   * DEV ONLY — returns a token for the demo tenant with admin scopes.
   * Used to bootstrap the UI without a real account during development.
   */
  devToken(role: string = "admin"): Promise<TokenPair> {
    return fetch(`${API_BASE_URL}/auth/dev-token?role=${encodeURIComponent(role)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json() as Promise<TokenPair>;
      });
  },

  /**
   * Discover the IdP's authorize endpoint so the UI can render an
   * "Войти через SSO" button. Returns `{ enabled: false }` when OIDC
   * isn't configured on the API.
   */
  oidcConfig(): Promise<OIDCConfigDto> {
    return fetch(`${API_BASE_URL}/auth/oidc/config`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json() as Promise<OIDCConfigDto>;
      });
  },
};
